import { invalidStrings } from '@ally/forms'
import { useBadWords } from './use-bad-words'

interface Validations {
  validateBadWords: (str: string, message: string) => string
  validateSpendingBucketNameChars: (str: string) => string
  validateAccountNicknameChars: (str: string) => string
  validateStkBucketNameChars: (str: string) => string
  validateStkNickName: (str: string) => string
  validateIsAllNumbers: (str: string) => boolean
}

const useValidation = (): Validations => {
  const badWords = useBadWords()
  const invalidStringsValidator = invalidStrings.validator(badWords)

  // matching bucket validation pattern from Savings Toolkit bucket names
  const validateSpendingBucketNameChars = (str: string): string => {
    const INPUT_WHITELIST = '^[A-Za-z0-9 ()!@\\-_+{}\\[\\]]+$'
    // Returns true if string is good, false if string is bad
    const matchesWhiteList = RegExp(INPUT_WHITELIST).test(str)

    // Found a special character that isn't allowed
    if (!matchesWhiteList) {
      return "Your bucket name can't contain special characters."
    }

    return ''
  }

  const validateAccountNicknameChars = (str: string): string => {
    if (/[&<>"/#~|^]/.test(str)) {
      return 'This field can\'t contain the following invalid characters: & < > " / # ~ | ^'
    }
    return ''
  }

  const validateBadWords = (str: string, message: string): string => {
    const isNotBadWord = invalidStringsValidator(str)
    if (!isNotBadWord) {
      return message
    }
    return ''
  }

  const validateStkBucketNameChars = (str: string): string => {
    // Matches all non-English characters and symbols OR <, >, %, &, /, \, #, ", ~, |, and ^
    /** \u2018 and \u2019 are angled apostrophes (’), which are default of iOS devices - these will need to be replaced with
    straight apostrophes for service calls! */
    // eslint-disable-next-line
    if (/[<>%&\/\\\#"~|\^]|[^\x00-\x7F \u2018\u2019]/g.test(str)) {
      return "Your name can't contain special characters like '# / \\ % &'"
    }
    return ''
  }

  const validateStkNickName = (str: string): string => {
    return (
      validateBadWords(
        str,
        "Try again, only using words you'd use in front of your boss.",
      ) || validateStkBucketNameChars(str)
    )
  }

  const validateIsAllNumbers = (str: string): boolean => {
    if (/^[0-9]*$/.test(str)) {
      return true
    }
    return false
  }

  return {
    validateBadWords,
    validateSpendingBucketNameChars,
    validateAccountNicknameChars,
    validateStkBucketNameChars,
    validateStkNickName,
    validateIsAllNumbers,
  }
}

export { useValidation }
